import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import GlobalNavSidebar from './GlobalNavSidebar';
import MinimalTopBar from './MinimalTopBar';
import Footer from './footer';
import { getItem, setItem } from '../../utils/localStorage';
const SIDEBAR_EXPANDED_KEY = 'truqc-ui-sidebar-expanded';
const MOBILE_MAX_WIDTH = 767;
const MOBILE_MQ = `(max-width: ${MOBILE_MAX_WIDTH}px)`;
const getIsMobileViewport = () => {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function') {
    return window.matchMedia(MOBILE_MQ).matches;
  }
  return typeof window.innerWidth === 'number' && window.innerWidth <= MOBILE_MAX_WIDTH;
};

/**
 * Shell component for sidebar layout.
 * Renders: fixed icon nav sidebar + thin top bar + main content area + footer.
 * Manages sidebar expanded/drawer state.
 */
const SidebarLayout = ({
  children
}) => {
  const {
    pathname
  } = useLocation();
  const [expanded, setExpanded] = useState(() => getItem(SIDEBAR_EXPANDED_KEY) === 'true');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => getIsMobileViewport());
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (typeof window.matchMedia === 'function') {
      const mq = window.matchMedia(MOBILE_MQ);
      const onChange = () => setIsMobile(mq.matches);
      onChange();
      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
      }
      mq.addListener(onChange);
      return () => mq.removeListener(onChange);
    }
    const onResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_MAX_WIDTH);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);
  useEffect(() => {
    if (!drawerOpen || typeof document === 'undefined') return undefined;
    const onKeyDown = event => {
      if (event.key === 'Escape') {
        setDrawerOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen]);
  const handleToggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    setItem(SIDEBAR_EXPANDED_KEY, String(next));
  };
  const handleOpenDrawer = () => setDrawerOpen(true);
  const handleCloseDrawer = () => setDrawerOpen(false);
  const mobileNavHidden = isMobile && !drawerOpen;
  return /*#__PURE__*/React.createElement("div", {
    className: `sidebar-layout${expanded ? ' sidebar-layout--expanded' : ''}`
  }, drawerOpen && /*#__PURE__*/React.createElement("div", {
    className: "sidebar-layout__backdrop",
    "data-testid": "sidebar-backdrop",
    role: "button",
    tabIndex: 0,
    "aria-label": "Close navigation",
    onClick: handleCloseDrawer,
    onKeyDown: event => {
      const {
        key
      } = event;
      if (key === 'Escape' || key === 'Enter' || key === ' ' || key === 'Spacebar') {
        if (key === ' ' || key === 'Spacebar') {
          event.preventDefault();
        }
        handleCloseDrawer();
      }
    }
  }), /*#__PURE__*/React.createElement(GlobalNavSidebar, {
    expanded: expanded,
    drawerOpen: drawerOpen,
    mobileNavHidden: mobileNavHidden,
    onToggleExpanded: handleToggleExpanded,
    onCloseDrawer: handleCloseDrawer
  }), /*#__PURE__*/React.createElement("div", {
    className: "sidebar-layout__main"
  }, /*#__PURE__*/React.createElement(MinimalTopBar, {
    drawerOpen: drawerOpen,
    onOpenDrawer: handleOpenDrawer,
    onCloseDrawer: handleCloseDrawer
  }), /*#__PURE__*/React.createElement("div", {
    className: "sidebar-layout__content"
  }, children), /*#__PURE__*/React.createElement(Footer, null)));
};
SidebarLayout.propTypes = {
  children: PropTypes.node.isRequired
};
export default SidebarLayout;